// apps/web/src/store/serializer.ts
import { applicationUpdateSchema, type ApplicationUpdate, type DbUser } from '@leangap/types'
import { migrateApplicationStatus } from '@/utils/backwardsCompatibility'

const keys: (keyof Omit<ApplicationUpdate, 'application_status'>)[] = [
	// general / student
	'name',
	'last_name',
	'born_date',
	'age',
	'gender',
	'country',
	'city',
	'zip_code',
	'phone',
	// high school
	'school_name',
	'school_country',
	'school_city',
	'school_zip_code',
	'school_summer_period',
	// parent
	'parent_name',
	'parent_last_name',
	'parent_email',
	'parent_phone',
	'parent_type_relation',
	'parent_relation',
	// other
	'has_disability',
	'disability',
	// background
	'share_proud',
	'share_reason_to_apply',
	'video_intro',
	'video_intro_is_public',
	'share_what_want_to_learn',
	'share_practical_skills',
	'share_extracurricular_activities',
	'share_entrepreneurial_type',
	'share_what_makes_teenager',
	'share_how_hear_about_us',
	'share_how_hear_about_us_extra',
	'share_something_to_know_about',
	// program
	'program_name',
	'priority_choice',
	'summer_commitments',
	// aid
	'is_required_aid',
	'aid_amount',
	'aid_currency',
	'aid_file_id',
	'aid_is_terms_accepted',
	// file
	'file_path',
	'file_original',
	'file_mime',
	'file_size',
]

const fieldSchemas = applicationUpdateSchema.shape

// --- BACKWARDS COMPATIBILITY ---
// This call migrates legacy array application_status to object format for old users.
// Once all users are migrated, REMOVE the call to migrateApplicationStatus and this comment.
export function serializeApplicationUpdate(u: DbUser): ApplicationUpdate {
	const migrated = migrateApplicationStatus(u)
	const out: Record<string, unknown> = {}
	for (const k of keys) {
		if (!Object.prototype.hasOwnProperty.call(migrated, k)) continue
		const value = migrated[k as keyof DbUser]
		// Drafts can hold half-typed answers (a one-letter name, a parent email
		// without a domain). The API rejects the whole save if any field fails,
		// so leave invalid fields out: the rest still saves and the server keeps
		// the last valid value for the skipped ones.
		if (!fieldSchemas[k].safeParse(value).success) continue
		out[k] = value
	}
	// Only include named keys for application_status
	if (migrated.application_status) {
		const s = migrated.application_status as Record<string, unknown>
		out.application_status = {
			general: Boolean(s['general']),
			background: Boolean(s['background']),
			program: Boolean(s['program']),
			aid: Boolean(s['aid']),
		}
	}
	return out as ApplicationUpdate
}
